import { useState } from 'react';
import dayjs from 'dayjs';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Box,
  Paper,
  Chip,
  Stack,
  Typography,
  Tooltip,
  TextField,
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TableViewIcon from '@mui/icons-material/TableView';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SearchIcon from '@mui/icons-material/Search';

import { useNavigate } from 'react-router-dom';
import { useEffectAsync } from '../reactHelper';
import fetchOrThrow from '../common/util/fetchOrThrow';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import useSettingsStyles from './common/useSettingsStyles';
import SearchHeader, { filterByKeyword } from './components/SearchHeader';
import TableShimmer from '../common/components/TableShimmer';
import { MAINTENANCE_ENDPOINTS } from './maintenanceConstants';

// já estão no package.json do Traccar
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const MaintenanceHistoryPage = () => {
  const { classes } = useSettingsStyles();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchKeyword, setSearchKeyword] = useState('');
  const [filterDeviceId, setFilterDeviceId] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  // período
  const todayStr = dayjs().format('YYYY-MM-DD');
  const [periodPreset, setPeriodPreset] = useState('today'); // today | yesterday | last7 | thisMonth | lastMonth | custom
  const [dateFrom, setDateFrom] = useState(todayStr);
  const [dateTo, setDateTo] = useState(todayStr);

  // só mostra resultados depois que clicar em "Mostrar"
  const [hasSearched, setHasSearched] = useState(false);

  // --------------------------------------------------------
  // Helpers de formatação
  // --------------------------------------------------------
  const formatDate = (date) => (date ? dayjs(date).format('DD/MM/YYYY') : '');

  const formatNumber = (value) =>
    value != null && value !== ''
      ? new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(
        Number(value),
      )
      : '';

  const formatCurrency = (value) =>
    value != null && value !== ''
      ? new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(Number(value))
      : '';

  const renderStatusChip = (status) => {
    if (!status) return null;

    let color = 'default';
    if (status === 'Planejado') color = 'warning';
    if (status === 'Concluido') color = 'success';
    if (status === 'Cancelado') color = 'error';

    return (
      <Chip
        label={status}
        size="small"
        color={color}
        variant="outlined"
      />
    );
  };

  const getDueStatus = (item) => {
    const today = dayjs().startOf('day');

    if (item.nextDate) {
      const next = dayjs(item.nextDate).startOf('day');
      const diff = next.diff(today, 'day');

      if (diff < 0) {
        return { label: 'Vencido', color: 'error' };
      }
      if (diff <= 7) {
        return { label: 'Próximo', color: 'warning' };
      }
      return { label: 'No prazo', color: 'success' };
    }

    if (
      item.nextOdometer != null &&
      item.nextOdometer !== '' &&
      item.odometer != null &&
      item.odometer !== ''
    ) {
      const current = Number(item.odometer) || 0;
      const next = Number(item.nextOdometer) || 0;
      const diffKm = next - current;

      if (diffKm < 0) {
        return { label: 'Vencido', color: 'error' };
      }
      if (diffKm <= 500) {
        return { label: 'Próximo', color: 'warning' };
      }
      return { label: 'No prazo', color: 'success' };
    }

    return { label: '—', color: 'default' };
  };

  const renderDueChip = (item) => {
    const { label, color } = getDueStatus(item);
    if (label === '—') {
      return <Typography variant="body2">—</Typography>;
    }
    return (
      <Chip
        label={label}
        size="small"
        color={color}
        variant="filled"
      />
    );
  };

  const calcIntervalKmHoras = (item) => {
    if (
      item.nextOdometer != null &&
      item.nextOdometer !== '' &&
      item.odometer != null &&
      item.odometer !== ''
    ) {
      return Number(item.nextOdometer) - Number(item.odometer);
    }
    return '';
  };

  // --------------------------------------------------------
  // Carregamento inicial
  // --------------------------------------------------------
  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetchOrThrow(MAINTENANCE_ENDPOINTS.maintenanceHistory);
      const data = await response.json();
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffectAsync(async () => {
    const response = await fetchOrThrow(MAINTENANCE_ENDPOINTS.devices);
    const data = await response.json();
    setDevices(data);
  }, []);

  // --------------------------------------------------------
  // Período
  // --------------------------------------------------------
  const applyPeriodPreset = (presetValue) => {
    setPeriodPreset(presetValue);

    const today = dayjs().startOf('day');

    if (presetValue === 'today') {
      const d = today.format('YYYY-MM-DD');
      setDateFrom(d);
      setDateTo(d);
      return;
    }

    if (presetValue === 'yesterday') {
      const d = today.subtract(1, 'day').format('YYYY-MM-DD');
      setDateFrom(d);
      setDateTo(d);
      return;
    }

    if (presetValue === 'last7') {
      const from = today.subtract(6, 'day').format('YYYY-MM-DD');
      const to = today.format('YYYY-MM-DD');
      setDateFrom(from);
      setDateTo(to);
      return;
    }

    if (presetValue === 'thisMonth') {
      const from = today.startOf('month').format('YYYY-MM-DD');
      const to = today.endOf('month').format('YYYY-MM-DD');
      setDateFrom(from);
      setDateTo(to);
      return;
    }

    if (presetValue === 'lastMonth') {
      const base = today.subtract(1, 'month');
      const from = base.startOf('month').format('YYYY-MM-DD');
      const to = base.endOf('month').format('YYYY-MM-DD');
      setDateFrom(from);
      setDateTo(to);
      return;
    }

    // custom -> usuário controla as datas
  };

  const handlePeriodPresetChange = (event) => {
    const value = event.target.value;
    applyPeriodPreset(value);
  };

  const handleChangeDateFrom = (event) => {
    setDateFrom(event.target.value);
    setPeriodPreset('custom');
  };

  const handleChangeDateTo = (event) => {
    setDateTo(event.target.value);
    setPeriodPreset('custom');
  };

  // --------------------------------------------------------
  // Filtros / KPIs
  // --------------------------------------------------------
  const applyDateFilter = (item) => {
    if (!dateFrom && !dateTo) return true;
    if (!item.date) return true;

    const d = dayjs(item.date).startOf('day');
    if (!d.isValid()) return true;

    if (dateFrom && d.isBefore(dayjs(dateFrom).startOf('day'))) {
      return false;
    }
    if (dateTo && d.isAfter(dayjs(dateTo).endOf('day'))) {
      return false;
    }

    return true;
  };

  const baseFilteredItems = items
    .filter(filterByKeyword(searchKeyword))
    .filter((item) => !filterDeviceId || String(item.deviceId) === String(filterDeviceId))
    .filter((item) => !filterStatus || item.status === filterStatus)
    .filter((item) => !filterCategory || item.category === filterCategory)
    .filter(applyDateFilter);

  const filteredItems = hasSearched ? baseFilteredItems : [];

  const totalRegistros = filteredItems.length;
  const totalValor = filteredItems.reduce(
    (acc, curr) => acc + (Number(curr.totalCost) || 0),
    0,
  );

  const statusCounts = filteredItems.reduce(
    (acc, item) => {
      if (item.status === 'Planejado') acc.planned += 1;
      if (item.status === 'Concluido') acc.done += 1;
      if (item.status === 'Cancelado') acc.cancelled += 1;
      return acc;
    },
    { planned: 0, done: 0, cancelled: 0 },
  );

  const hasData = filteredItems.length > 0;

  // --------------------------------------------------------
  // Navegação + CRUD
  // --------------------------------------------------------
  const openEdit = (id) => {
    navigate(`/settings/maintenance/history/${id}`);
  };

  const openCreate = () => {
    navigate('/settings/maintenance/history/new');
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Tem certeza que deseja excluir esta manutenção?');
    if (!confirmed) return;

    await fetchOrThrow(`/maintenance-api/maintenance-records/${id}`, {
      method: 'DELETE',
    });

    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleShow = () => {
    setHasSearched(true);
  };

  // --------------------------------------------------------
  // Dados base para exportação
  // --------------------------------------------------------
  const buildExportRows = () =>
    filteredItems.map((item) => {
      const due = getDueStatus(item);
      const intervalo = calcIntervalKmHoras(item);

      return {
        veiculo: item.deviceName || '',
        servico: item.service || '',
        categoria: item.category || '',
        status: item.status || '',
        data: formatDate(item.date),
        proximaData: formatDate(item.nextDate),
        kmHoras: item.odometer || '',
        proxKmHoras: item.nextOdometer || '',
        intervaloKmHoras: intervalo,
        situacao: due.label,
        valor: item.totalCost || '',
      };
    });

  // --------------------------------------------------------
  // Exportar CSV
  // --------------------------------------------------------
  const handleExportCsv = () => {
    if (!hasData) return;

    const rows = buildExportRows();
    const header = [
      'Veiculo',
      'Servico',
      'Categoria',
      'Status',
      'Data',
      'ProximaData',
      'KmHoras',
      'ProxKmHoras',
      'IntervaloKmHoras',
      'Situacao',
      'Valor',
    ];

    const separator = ';';
    const csvLines = [];

    csvLines.push(header.join(separator));

    rows.forEach((r) => {
      const line = [
        r.veiculo,
        r.servico,
        r.categoria,
        r.status,
        r.data,
        r.proximaData,
        r.kmHoras,
        r.proxKmHoras,
        r.intervaloKmHoras,
        r.situacao,
        r.valor,
      ]
        .map((field) => `"${String(field ?? '').replace(/"/g, '""')}"`)
        .join(separator);

      csvLines.push(line);
    });

    const csvString = `\uFEFF${csvLines.join('\r\n')}`;
    const blob = new Blob([csvString], {
      type: 'text/csv;charset=utf-8;',
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = dayjs().format('YYYYMMDD-HHmm');
    link.href = url;
    link.setAttribute('download', `historico-manutencao-${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // --------------------------------------------------------
  // Exportar Excel (.xlsx)
  // --------------------------------------------------------
  const handleExportExcel = async () => {
    if (!hasData) return;

    const rows = buildExportRows();
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Histórico');

    sheet.columns = [
      { header: 'Veículo', key: 'veiculo', width: 25 },
      { header: 'Serviço', key: 'servico', width: 30 },
      { header: 'Categoria', key: 'categoria', width: 15 },
      { header: 'Status', key: 'status', width: 14 },
      { header: 'Data', key: 'data', width: 14 },
      { header: 'Próx. Data', key: 'proximaData', width: 14 },
      { header: 'Km/Horas', key: 'kmHoras', width: 14 },
      { header: 'Próx. Km/Horas', key: 'proxKmHoras', width: 16 },
      { header: 'Intervalo Km/Horas', key: 'intervaloKmHoras', width: 18 },
      { header: 'Situação', key: 'situacao', width: 16 },
      { header: 'Valor', key: 'valor', width: 14 },
    ];

    rows.forEach((r) => {
      sheet.addRow({
        veiculo: r.veiculo,
        servico: r.servico,
        categoria: r.categoria,
        status: r.status,
        data: r.data,
        proximaData: r.proximaData,
        kmHoras: r.kmHoras ? Number(r.kmHoras) : null,
        proxKmHoras: r.proxKmHoras ? Number(r.proxKmHoras) : null,
        intervaloKmHoras: r.intervaloKmHoras ? Number(r.intervaloKmHoras) : null,
        situacao: r.situacao,
        valor: r.valor ? Number(r.valor) : null,
      });
    });

    sheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFEEEEEE' },
      };
    });

    sheet.getColumn('kmHoras').numFmt = '#,##0';
    sheet.getColumn('proxKmHoras').numFmt = '#,##0';
    sheet.getColumn('intervaloKmHoras').numFmt = '#,##0';
    sheet.getColumn('valor').numFmt = 'R$ #,##0.00';

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const timestamp = dayjs().format('YYYYMMDD-HHmm');
    saveAs(blob, `historico-manutencao-${timestamp}.xlsx`);
  };

  // --------------------------------------------------------
  // "Exportar PDF" via janela de impressão
  // --------------------------------------------------------
  const handleExportPdf = () => {
    if (!hasData) return;

    const rows = buildExportRows();
    const timestamp = dayjs().format('DD/MM/YYYY HH:mm');

    const html = `
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Histórico de Manutenção</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              font-size: 10pt;
              padding: 16px;
            }
            h1 {
              font-size: 16pt;
              margin-bottom: 4px;
            }
            .subtitle {
              font-size: 9pt;
              color: #555;
              margin-bottom: 12px;
            }
            table {
              border-collapse: collapse;
              width: 100%;
            }
            th, td {
              border: 1px solid #ccc;
              padding: 4px 6px;
              text-align: left;
            }
            th {
              background-color: #f0f0f0;
            }
            td.num {
              text-align: right;
            }
          </style>
        </head>
        <body>
          <h1>Histórico de Manutenção</h1>
          <div class="subtitle">Gerado em ${timestamp}</div>
          <table>
            <thead>
              <tr>
                <th>Veículo</th>
                <th>Serviço</th>
                <th>Categoria</th>
                <th>Status</th>
                <th>Data</th>
                <th>Próx. Data</th>
                <th>Km/Horas</th>
                <th>Próx. Km/Horas</th>
                <th>Intervalo Km/Horas</th>
                <th>Situação</th>
                <th>Valor</th>
              </tr>
            </thead>
            <tbody>
              ${rows
                .map(
                  (r) => `
                    <tr>
                      <td>${r.veiculo}</td>
                      <td>${r.servico}</td>
                      <td>${r.categoria}</td>
                      <td>${r.status}</td>
                      <td>${r.data}</td>
                      <td>${r.proximaData}</td>
                      <td class="num">${r.kmHoras}</td>
                      <td class="num">${r.proxKmHoras}</td>
                      <td class="num">${r.intervaloKmHoras}</td>
                      <td>${r.situacao}</td>
                      <td class="num">${formatCurrency(r.valor)}</td>
                    </tr>
                  `,
                )
                .join('')}
            </tbody>
          </table>
        </body>
      </html>
    `;

    const win = window.open('', '_blank');
    if (!win) {
      const blob = new Blob([html], { type: 'text/html;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      return;
    }

    win.document.open();
    win.document.write(html);
    win.document.close();

    setTimeout(() => {
      win.focus();
      win.print();
    }, 500);
  };

  // --------------------------------------------------------
  // Render
  // --------------------------------------------------------
  return (
    <PageLayout
      menu={<SettingsMenu />}
      breadcrumbs={['settingsTitle', 'sharedMaintenance']}
    >
      {/* Busca textual (serviço, oficina, observações, etc) */}
      <SearchHeader keyword={searchKeyword} setKeyword={setSearchKeyword} />

      {/* Filtros + resumo + ações */}
      <Paper
        elevation={2}
        sx={{
          mt: 2,
          mb: 2,
          p: 2,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          flexWrap="wrap"
          alignItems="center"
        >
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Veículo</InputLabel>
            <Select
              label="Veículo"
              value={filterDeviceId}
              onChange={(e) => setFilterDeviceId(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {devices.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Categoria</InputLabel>
            <Select
              label="Categoria"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="Preventiva">Preventiva</MenuItem>
              <MenuItem value="Corretiva">Corretiva</MenuItem>
              <MenuItem value="Legal">Legal</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="Planejado">Planejado</MenuItem>
              <MenuItem value="Concluido">Concluído</MenuItem>
              <MenuItem value="Cancelado">Cancelado</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Período</InputLabel>
            <Select
              label="Período"
              value={periodPreset}
              onChange={handlePeriodPresetChange}
            >
              <MenuItem value="today">Hoje</MenuItem>
              <MenuItem value="yesterday">Ontem</MenuItem>
              <MenuItem value="last7">Últimos 7 dias</MenuItem>
              <MenuItem value="thisMonth">Este mês</MenuItem>
              <MenuItem value="lastMonth">Mês anterior</MenuItem>
              <MenuItem value="custom">Personalizado</MenuItem>
            </Select>
          </FormControl>

          <TextField
            type="date"
            size="small"
            label="De"
            value={dateFrom}
            onChange={handleChangeDateFrom}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            type="date"
            size="small"
            label="Até"
            value={dateTo}
            onChange={handleChangeDateTo}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>

        <Stack
          direction="row"
          spacing={3}
          alignItems="center"
          sx={{ mt: { xs: 2, md: 0 } }}
        >
          <Box sx={{ textAlign: 'right', minWidth: 90 }}>
            <Typography variant="caption" color="text.secondary">
              Filtradas
            </Typography>
            <Typography variant="subtitle1">
              {totalRegistros}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right', minWidth: 140 }}>
            <Typography variant="caption" color="text.secondary">
              Total em serviços
            </Typography>
            <Typography variant="subtitle1">
              {formatCurrency(totalValor)}
            </Typography>
          </Box>

          <Stack direction="row" spacing={2}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary">
                Planejadas
              </Typography>
              <Typography variant="body2">
                {statusCounts.planned}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary">
                Concluídas
              </Typography>
              <Typography variant="body2">
                {statusCounts.done}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="caption" color="text.secondary">
                Canceladas
              </Typography>
              <Typography variant="body2">
                {statusCounts.cancelled}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<SearchIcon />}
              onClick={handleShow}
            >
              Mostrar
            </Button>

            <Tooltip title="Exportar CSV (texto)">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<TableViewIcon />}
                  onClick={handleExportCsv}
                  disabled={!hasData}
                >
                  CSV
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Exportar Excel (.xlsx)">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportExcel}
                  disabled={!hasData}
                >
                  Excel
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Exportar PDF">
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleExportPdf}
                  disabled={!hasData}
                >
                  PDF
                </Button>
              </span>
            </Tooltip>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreate}
            >
              Nova manutenção
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Tabela de resultados */}
      <Table className={classes.table}>
        <TableHead>
          <TableRow>
            <TableCell>Veículo</TableCell>
            <TableCell>Serviço</TableCell>
            <TableCell>Categoria</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Data</TableCell>
            <TableCell>Próx. Data</TableCell>
            <TableCell align="right">Km / Horas</TableCell>
            <TableCell align="right">Próx. Km / Horas</TableCell>
            <TableCell>Situação</TableCell>
            <TableCell align="right">Valor</TableCell>
            <TableCell className={classes.columnAction} />
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableShimmer columns={11} endAction />
          ) : !hasSearched ? (
            <TableRow>
              <TableCell colSpan={11}>
                <Box
                  sx={{
                    py: 4,
                    textAlign: 'center',
                    color: 'text.secondary',
                  }}
                >
                  Selecione os filtros acima e clique em <strong>Mostrar</strong> para ver o histórico de manutenção.
                </Box>
              </TableCell>
            </TableRow>
          ) : hasData ? (
            filteredItems.map((item) => (
              <TableRow key={item.id} hover>
                <TableCell>{item.deviceName}</TableCell>
                <TableCell>{item.service}</TableCell>
                <TableCell>{item.category}</TableCell>
                <TableCell>{renderStatusChip(item.status)}</TableCell>
                <TableCell>{formatDate(item.date)}</TableCell>
                <TableCell>{formatDate(item.nextDate)}</TableCell>
                <TableCell align="right">
                  {formatNumber(item.odometer)}
                </TableCell>
                <TableCell align="right">
                  {formatNumber(item.nextOdometer)}
                </TableCell>
                <TableCell>
                  {renderDueChip(item)}
                </TableCell>
                <TableCell align="right">
                  {formatCurrency(item.totalCost)}
                </TableCell>
                <TableCell className={classes.columnAction} padding="none">
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Tooltip title="Editar manutenção">
                      <IconButton
                        onClick={() => openEdit(item.id)}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir manutenção">
                      <IconButton
                        onClick={() => handleDelete(item.id)}
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={11}>
                <Box
                  sx={{
                    py: 4,
                    textAlign: 'center',
                    color: 'text.secondary',
                  }}
                >
                  Nenhuma manutenção encontrada com os filtros selecionados.
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </PageLayout>
  );
};

export default MaintenanceHistoryPage;
